/** In-memory + session cache for venue booking lists (Home / Manage — not refetched on tab clicks). */

export const VENUE_BOOKINGS_CACHE_TTL_MS = 5 * 60 * 1000;
const SESSION_PREFIX = 'occasia:venue-bookings-list:v2:';

const memoryCache = new Map();

const readSessionEntry = (venueId) => {
  try {
    const raw = sessionStorage.getItem(`${SESSION_PREFIX}${venueId}`);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const writeSessionEntry = (venueId, entry) => {
  try {
    sessionStorage.setItem(`${SESSION_PREFIX}${venueId}`, JSON.stringify(entry));
  } catch {
    // quota / private mode — memory cache still works
  }
};

const removeSessionEntry = (venueId) => {
  try {
    sessionStorage.removeItem(`${SESSION_PREFIX}${venueId}`);
  } catch {
    // no-op
  }
};

export const getVenueBookingsCacheEntry = (venueId) => {
  if (!venueId) return null;
  const key = String(venueId);
  const mem = memoryCache.get(key);
  if (mem?.bookings?.length) return mem;
  const session = readSessionEntry(key);
  if (session?.bookings?.length) {
    memoryCache.set(key, session);
    return session;
  }
  return mem || session || null;
};

export const isVenueBookingsCacheFresh = (venueId, ttlMs = VENUE_BOOKINGS_CACHE_TTL_MS) => {
  const entry = getVenueBookingsCacheEntry(venueId);
  if (!entry?.fetchedAt) return false;
  return Date.now() - entry.fetchedAt <= ttlMs;
};

export const setVenueBookingsCacheEntry = (venueId, payload) => {
  if (!venueId) return;
  const key = String(venueId);
  const entry = {
    bookings: payload.bookings || [],
    fetchedAt: payload.fetchedAt ?? Date.now(),
    pastOffset: payload.pastOffset ?? 0,
    hasMorePast: payload.hasMorePast ?? true,
    pastCutoffYmd: payload.pastCutoffYmd ?? null,
    manageSeedLoaded: payload.manageSeedLoaded ?? false
  };
  memoryCache.set(key, entry);
  writeSessionEntry(key, entry);
};

export const patchVenueBookingsCacheBookings = (venueId, bookings) => {
  const prev = getVenueBookingsCacheEntry(venueId);
  if (!prev) return;
  setVenueBookingsCacheEntry(venueId, { ...prev, bookings, fetchedAt: prev.fetchedAt });
};

export const invalidateVenueBookingsCache = (venueId) => {
  if (!venueId) {
    memoryCache.clear();
    return;
  }
  const key = String(venueId);
  memoryCache.delete(key);
  removeSessionEntry(key);
};
